import { twMerge } from 'tailwind-merge';
import MediaDisplay from '~/components/MediaDisplay';
import { useCurrencyFormatter } from '~/utils/formatters';
import { AdvancedExchangeMethod } from '~/utils/schemas';
import { Product } from './exchangeOptions';

function computeStartingPrice(variants: Product['variants']) {
  const prices = variants.map((v) => v.price);
  const minPrice = Math.min(...prices);
  return minPrice > 0 ? minPrice : 0;
}

export default function ProductDisplay({
  product,
  banner,
  onSelect: onClick,
  advancedExchangeMethod,
}: {
  product: Product;
  onSelect?: () => void;
  banner?: string | null;
  advancedExchangeMethod: AdvancedExchangeMethod;
}) {
  const startingPrice = computeStartingPrice(product.variants);
  const currencyFormatter = useCurrencyFormatter();

  // TODO elevate condition into shared value for showing pricing
  const showStartingPrice =
    advancedExchangeMethod === 'PAYMENT_AND_PARTIAL_REFUND' &&
    startingPrice !== 0;

  return (
    <button
      type="button"
      className="group flex cursor-pointer flex-col items-center gap-2 outline-hidden transition-transform hover:-translate-y-1"
      onClick={onClick}
    >
      <div
        data-component="corso-product-image"
        className={twMerge(
          'relative aspect-square max-h-40 min-h-20 w-full max-w-40 min-w-20 ring-brand transition-shadow',
          // the media inherits this radius, so the ring and image reshape together
          'rounded-lg',
          // TODO re-evaluate the styling of the hover state, to see if it should use the same clarity as the focus state
          'group-focus-within:ring-2 group-focus-within:ring-offset-2 group-hover:shadow-md',
        )}
      >
        <MediaDisplay
          alt={product.name}
          src={product.imageUrl}
          type="image"
          className="rounded-[inherit]"
        />
        {banner && (
          <p className="absolute right-0 bottom-0 left-0 rounded-b border-x border-x-outline-variant bg-inverse-surface p-1 text-center body-sm break-words text-inverse-on-surface">
            {banner}
          </p>
        )}
      </div>
      <p className="text-center label-md text-on-surface">{product.name}</p>
      {showStartingPrice && (
        <p className="text-center body-sm text-on-surface-variant">
          From {currencyFormatter(startingPrice)}
        </p>
      )}
    </button>
  );
}
